export type ProfileDetailRow = {
  key: string;
  label: string;
  value: string;
  /** Non-empty when the field is an object; rendered as an expandable group. */
  children?: ProfileDetailRow[];
};

// etag is an internal concurrency token for saves, not profile data. Only
// hidden at the top level: a nested key of the same name (metadata is
// merchant-controlled) is the merchant's own data.
const HIDDEN_TOP_LEVEL_KEYS = new Set(["etag"]);

const EMPTY_VALUE = "-";

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isEmpty(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  if (isPlainObject(value)) return Object.keys(value).length === 0;
  return false;
}

/** `firstName` / `created_at` → "First name" / "Created at". */
export function labelFromKey(key: string): string {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function formatValue(value: unknown): string {
  if (isEmpty(value)) return EMPTY_VALUE;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) {
    return value.every((item) => item === null || typeof item !== "object")
      ? value.join(", ")
      : JSON.stringify(value);
  }
  if (isPlainObject(value)) return JSON.stringify(value);
  return String(value).trim();
}

function buildRows(
  source: Record<string, unknown>,
  keyPrefix: string,
  topLevel: boolean,
): ProfileDetailRow[] {
  return Object.entries(source)
    .filter(([key]) => !topLevel || !HIDDEN_TOP_LEVEL_KEYS.has(key))
    .map(([key, value]) => {
      const rowKey = keyPrefix + key;
      const label = labelFromKey(key);
      if (topLevel && isPlainObject(value) && !isEmpty(value)) {
        const children = buildRows(value, `${rowKey}.`, false);
        // A group always has children to show; otherwise it would render an
        // expand toggle with nothing behind it.
        if (children.length > 0) {
          return { key: rowKey, label, value: "", children };
        }
      }
      return { key: rowKey, label, value: formatValue(value) };
    });
}

/**
 * Every field on the profile, in API order. Objects become one level of
 * expandable children; anything nested deeper is shown as JSON.
 */
export function buildProfileDetailRows(
  source: Record<string, unknown>,
): ProfileDetailRow[] {
  return buildRows(source, "", true);
}
